import { inject, Injectable, NgZone } from '@angular/core';
import { TokenStorage } from '@forgerock/javascript-sdk';

import { env } from '@amfam/environment';
import { DataDogService } from '@amfam/shared/analytics/lib/services/data-dog.service';
import { WindowRef } from '@amfam/shared/utility/shared-services/lib/window/window.service';

/**
 * Upper bound on waiting for a rebuilt widget to signal ready. `init` returns undefined and
 * reports completion only by re-emitting `onEmbeddedMessagingReady`, so without a deadline a
 * failed rebuild would leave callers awaiting forever.
 */
export const CHAT_REBUILD_READY_TIMEOUT_MS = 15_000;

/**
 * Salesforce Embedded Messaging injects `embeddedservice_bootstrap` onto `window` at runtime
 * via a dynamically loaded script. No first-party types are published, so only the surface
 * we use is declared here.
 */
export interface SalesforceWindow {
  embeddedservice_bootstrap?: {
    settings: { language: string; hiddenPrechatFields: Record<string, string> };
    userVerificationAPI?: {
      clearSession?: (shouldEndSession?: boolean) => Promise<unknown>;
      setIdentityToken: (config: { identityTokenType: string; identityToken: string }) => void;
    };
    init: (
      orgId: string,
      deploymentName: string,
      siteUrl: string,
      snippetConfig: { scrt2URL: string }
    ) => void;
    removeMarkup?: () => void;
  };
}

/**
 * Owns the lifecycle of the Salesforce Embedded Messaging session: standing the widget up,
 * tearing a restored session down, and verifying the current customer with it.
 */
@Injectable({ providedIn: 'root' })
export class SalesforceChatSessionService {
  private win = inject(WindowRef);
  private ngZone = inject(NgZone);
  private dataDogService = inject(DataDogService);

  private activeReset: Promise<void> | undefined;

  /**
   * True while a reset is tearing the widget down and rebuilding it. The rebuild re-emits
   * `onEmbeddedMessagingReady`, so ready listeners use this to ignore events belonging to a
   * widget that is mid-replacement.
   */
  get isResetting(): boolean {
    return !!this.activeReset;
  }

  private get bootstrap(): NonNullable<SalesforceWindow['embeddedservice_bootstrap']> | undefined {
    return (this.win.nativeWindow as unknown as SalesforceWindow).embeddedservice_bootstrap;
  }

  /** Boots the widget with this environment's deployment. */
  init(): void {
    this.bootstrap?.init(
      env.salesforceChat.orgId,
      env.salesforceChat.deploymentName,
      env.salesforceChat.siteUrl,
      {
        scrt2URL: env.salesforceChat.scrt2Url
      }
    );
  }

  /**
   * Ends the current chat session and replaces the widget with a freshly verified one.
   * Concurrent callers share one rebuild.
   *
   * Resolves even on failure. Both callers are fire-and-forget — a window event handler and
   * an idle timer — with nowhere to put an error, so the contract is enforced here rather
   * than trusted at each call site.
   */
  reset(): Promise<void> {
    this.activeReset ??= this.runReset()
      .catch(error => {
        this.dataDogService.log('Salesforce chat session reset failed', 'error', {
          error: error instanceof Error ? error.message : error
        });
      })
      .finally(() => {
        this.activeReset = undefined;
      });
    return this.activeReset;
  }

  /**
   * Verifies the signed-in customer with the widget, so the conversation is attributed to
   * them rather than started anonymously. Never rejects.
   */
  async applyIdentityToken(): Promise<void> {
    try {
      const tokens = await TokenStorage.get();

      if (!tokens?.idToken) {
        this.dataDogService.log('No ForgeRock ID token available for Salesforce chat', 'warn');
        return;
      }

      const userVerificationAPI = this.bootstrap?.userVerificationAPI;

      if (!userVerificationAPI?.setIdentityToken) {
        this.dataDogService.log('userVerificationAPI.setIdentityToken not available', 'warn');
        return;
      }

      userVerificationAPI.setIdentityToken({
        identityTokenType: 'JWT',
        identityToken: tokens.idToken
      });
    } catch (error) {
      this.dataDogService.log('Error setting identity token for Salesforce chat', 'error', {
        error: error instanceof Error ? error.message : error
      });
    }
  }

  private async runReset(): Promise<void> {
    const embeddedSvc = this.bootstrap;
    const userVerificationAPI = embeddedSvc?.userVerificationAPI;

    if (!embeddedSvc || !userVerificationAPI?.clearSession) {
      this.dataDogService.log('Salesforce chat clearSession unavailable', 'warn');
      return;
    }

    try {
      await userVerificationAPI.clearSession(true);
    } catch (error) {
      // Rebuild anyway. The teardown below is what actually evicts the restored
      // conversation, so a failed clear is a reason to continue, not to stop.
      this.dataDogService.log('Salesforce chat clearSession failed', 'warn', {
        error: error instanceof Error ? error.message : error
      });
    }

    // Undocumented API, so degrade instead of throwing if a future bundle drops it.
    if (typeof embeddedSvc.removeMarkup !== 'function') {
      this.dataDogService.log(
        'Salesforce chat removeMarkup unavailable, widget not rebuilt',
        'warn'
      );
      return;
    }

    try {
      // Outside the Angular zone because `init` builds cross-origin iframes, and Zone.js
      // patching their async work surfaces SecurityErrors to Angular's ErrorHandler.
      await this.ngZone.runOutsideAngular(() => {
        // Subscribed before the teardown because `init` returns undefined and signals
        // completion only by re-emitting ready.
        const ready = this.nextReady();
        embeddedSvc.removeMarkup?.();
        this.init();
        return ready;
      });
      this.dataDogService.log('Salesforce chat widget rebuilt after session reset', 'info');
    } catch (error) {
      this.dataDogService.log('Salesforce chat widget rebuild failed', 'error', {
        error: error instanceof Error ? error.message : error
      });
      return;
    }

    await this.applyIdentityToken();
  }

  /** Resolves on the next `onEmbeddedMessagingReady`, or on timeout. Never rejects. */
  private nextReady(): Promise<void> {
    const win = this.win.nativeWindow;
    const controller = new AbortController();

    return new Promise<void>(resolve => {
      const timer = setTimeout(() => {
        controller.abort();
        this.dataDogService.log('Salesforce chat rebuild did not signal ready', 'warn');
        resolve();
      }, CHAT_REBUILD_READY_TIMEOUT_MS);

      win.addEventListener(
        'onEmbeddedMessagingReady',
        () => {
          clearTimeout(timer);
          resolve();
        },
        { once: true, signal: controller.signal }
      );
    });
  }
}
